import { FC, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Spinner from '../Spinner/Spinner';
import styles from './FilePreviewViewer.module.scss';

export const PREVIEWABLE_EXTENSIONS: Record<string, 'image' | 'pdf' | 'video' | 'text'> = {
  jpg: 'image', jpeg: 'image', png: 'image', gif: 'image', webp: 'image', svg: 'image', bmp: 'image',
  pdf: 'pdf',
  mp4: 'video', webm: 'video', ogg: 'video', mov: 'video',
  txt: 'text', json: 'text', xml: 'text', csv: 'text', md: 'text', log: 'text',
};

export const getFileExtension = (fileName: string): string => {
  return fileName?.slice(((fileName.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase();
};

export type PreviewType = 'image' | 'pdf' | 'video' | 'text';

export const getPreviewType = (fileName: string): PreviewType | null => {
  const ext = getFileExtension(fileName);
  return PREVIEWABLE_EXTENSIONS[ext] ?? null;
};

export const isPreviewable = (fileName: string): boolean => {
  return getPreviewType(fileName) !== null;
};

type PreviewContentState = {
  loading: boolean;
  error: string | null;
  /** Set for text type */
  textContent: string | null;
  /** Set for image/pdf/video (blob object URL) */
  blobUrl: string | null;
};

type PresentResult = { blobUrl: string } | { textContent: string };

/**
 * Takes a blob and preview type and returns the form needed to display it.
 * Single path for all previewable types: image, pdf, video, text.
 */
async function presentBlobForPreview(blob: Blob, previewType: PreviewType): Promise<PresentResult> {
  if (previewType === 'text') {
    const textContent = await blob.text();
    return { textContent };
  }
  const blobForUrl = previewType === 'pdf' ? new Blob([blob], { type: 'application/pdf' }) : blob;
  return { blobUrl: URL.createObjectURL(blobForUrl) };
}

/**
 * Single hook: fetch as blob, then present via presentBlobForPreview (one path for all types).
 */
export function usePreviewContent(url: string, previewType: PreviewType | null): PreviewContentState {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!url || !previewType) {
      setLoading(false);
      setTextContent(null);
      setBlobUrl(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setTextContent(null);
    setBlobUrl(null);
    let cancelled = false;

    axios.get(url, { responseType: 'blob' })
      .then(res => presentBlobForPreview(res.data, previewType))
      .then(result => {
        if (cancelled) return;
        if ('blobUrl' in result) {
          if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = result.blobUrl;
          setBlobUrl(result.blobUrl);
        } else {
          setTextContent(result.textContent);
        }
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = null;
        }
        setBlobUrl(null);
        setError(err.message);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [url, previewType]);

  return { loading, error, textContent, blobUrl };
}

type FilePreviewViewerProps = {
  url: string;
  fileName: string;
  previewType?: PreviewType | null;
  onClose?: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
};

export const FilePreviewViewer: FC<FilePreviewViewerProps> = ({
  url,
  fileName,
  previewType: previewTypeProp,
  onClose,
  onPrevious,
  onNext,
}) => {
  const previewType = previewTypeProp ?? getPreviewType(fileName);
  const { loading, error, textContent, blobUrl } = usePreviewContent(url, previewType);

  useEffect(() => {
    if (!onClose && !onPrevious && !onNext) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowLeft') onPrevious?.();
      else if (e.key === 'ArrowRight') onNext?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, onPrevious, onNext]);

  if (loading) return <Spinner size="xxs" progress={0} />;
  if (error) return <p style={{ color: 'var(--red-500)' }}>{error}</p>;
  if (!previewType) return null;

  switch (previewType) {
    case 'image':
      return <img src={blobUrl ?? ''} alt={fileName} className={styles.previewImage} />;
    case 'pdf':
      return <iframe src={blobUrl ?? ''} className={styles.previewPdf} title={fileName} />;
    case 'video':
      return <video src={blobUrl ?? ''} controls className={styles.previewVideo} />;
    case 'text':
      return <pre className={styles.previewText}>{textContent ?? ''}</pre>;
    default:
      return null;
  }
};
